import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWalletAuth } from '../../context/WalletAuthContext';
import { superAdminAPI } from '../../lib/walletApi';
import { Button } from '../../components/ui/button';
import { Loader2, Eye, EyeOff, Shield, ArrowLeft, Mail, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

export default function SuperAdminLogin() {
  const navigate = useNavigate();
  const { login } = useWalletAuth();
  const [step, setStep] = useState('credentials'); // 'credentials' | 'otp'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [resending, setResending] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const otpRefs = useRef([]);

  useEffect(() => {
    if (countdown > 0) {
      const t = setTimeout(() => setCountdown(c => c - 1), 1000);
      return () => clearTimeout(t);
    }
  }, [countdown]);

  const handleCredentials = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const resp = await superAdminAPI.init2FA(email, password);
      setStep('otp');
      setCountdown(60);
      toast.success('Verification code sent to your email');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (index, value) => {
    if (value.length > 1) value = value[value.length - 1];
    if (!/^\d*$/.test(value)) return;
    const next = [...otp];
    next[index] = value;
    setOtp(next);
    if (value && index < 5) otpRefs.current[index + 1]?.focus();
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    const paste = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (paste.length === 6) {
      setOtp(paste.split(''));
      otpRefs.current[5]?.focus();
    }
  };

  const handleVerify = async (e) => {
    e?.preventDefault();
    const code = otp.join('');
    if (code.length !== 6) { toast.error('Enter the 6-digit code'); return; }
    setLoading(true);
    try {
      const data = await superAdminAPI.verify2FA(email, code);
      login(data.access_token, data.user);
      navigate('/super-admin');
      toast.success('Welcome back!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid code');
      setOtp(['', '', '', '', '', '']);
      otpRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    try {
      await superAdminAPI.resend2FA(email);
      setCountdown(60);
      toast.success('New code sent');
    } catch { toast.error('Failed to resend'); }
    finally { setResending(false); }
  };

  // Auto-submit when all 6 digits entered
  useEffect(() => {
    if (otp.every(d => d !== '') && step === 'otp') handleVerify();
  }, [otp]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#1a1d1f] px-4" data-testid="super-admin-login-page">
      <div className="w-full max-w-[400px]">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-xl bg-[#D64D7A]/15 flex items-center justify-center">
            <Shield className="w-5 h-5 text-[#D64D7A]" />
          </div>
          <div>
            <p className="text-white font-bold text-lg" style={{ fontFamily: 'Manrope' }}>Pebbles Admin</p>
            <p className="text-[#6b7280] text-xs">Super administrator access</p>
          </div>
        </div>

        {step === 'credentials' ? (
          <form onSubmit={handleCredentials} className="space-y-4">
            <div>
              <label className="text-xs font-medium text-[#6b7280] mb-1.5 block">Email</label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} required
                placeholder="joshua@thcohq.com" data-testid="sa-login-email"
                className="w-full h-11 px-4 rounded-lg border border-white/10 bg-white/[0.06] text-sm text-white placeholder:text-[#6b7280] focus:outline-none focus:ring-2 focus:ring-[#D64D7A]/40 focus:border-[#D64D7A] transition-all" />
            </div>
            <div>
              <label className="text-xs font-medium text-[#6b7280] mb-1.5 block">Password</label>
              <div className="relative">
                <input type={showPassword ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} required
                  placeholder="Enter password" data-testid="sa-login-password"
                  className="w-full h-11 px-4 pr-11 rounded-lg border border-white/10 bg-white/[0.06] text-sm text-white placeholder:text-[#6b7280] focus:outline-none focus:ring-2 focus:ring-[#D64D7A]/40 focus:border-[#D64D7A] transition-all" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} data-testid="sa-toggle-password"
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-[#6b7280] hover:text-white transition-colors">
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <Button type="submit" disabled={loading} data-testid="sa-login-submit"
              className="w-full h-11 rounded-lg bg-[#D64D7A] hover:bg-[#c4406b] text-white text-sm font-semibold mt-2">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Continue'}
            </Button>
            <div className="text-center mt-3">
              <button type="button" onClick={() => navigate('/forgot-password')} className="text-xs text-[#D64D7A] hover:text-[#c4406b] font-medium transition-colors" data-testid="sa-forgot-password">
                Forgot password?
              </button>
            </div>
          </form>
        ) : (
          <div className="space-y-6">
            <button onClick={() => { setStep('credentials'); setOtp(['','','','','','']); }}
              className="flex items-center gap-1.5 text-xs text-[#6b7280] hover:text-white transition-colors" data-testid="sa-2fa-back">
              <ArrowLeft className="w-3.5 h-3.5" /> Back
            </button>

            <div className="text-center space-y-2">
              <div className="w-12 h-12 rounded-xl bg-[#D64D7A]/15 flex items-center justify-center mx-auto mb-3">
                <Mail className="w-6 h-6 text-[#D64D7A]" />
              </div>
              <h2 className="text-white text-lg font-semibold">Check your email</h2>
              <p className="text-[#6b7280] text-sm">
                We sent a 6-digit code to <span className="text-white font-medium">{email}</span>
              </p>
            </div>

            <form onSubmit={handleVerify} className="space-y-5">
              <div className="flex justify-center gap-2.5" onPaste={handleOtpPaste}>
                {otp.map((digit, i) => (
                  <input key={i} ref={el => otpRefs.current[i] = el} type="text" inputMode="numeric"
                    value={digit} onChange={e => handleOtpChange(i, e.target.value)}
                    onKeyDown={e => handleOtpKeyDown(i, e)} maxLength={1}
                    data-testid={`sa-otp-${i}`}
                    className="w-12 h-14 text-center text-xl font-bold rounded-lg border border-white/10 bg-white/[0.06] text-white focus:outline-none focus:ring-2 focus:ring-[#D64D7A]/40 focus:border-[#D64D7A] transition-all" />
                ))}
              </div>

              <Button type="submit" disabled={loading || otp.some(d => !d)} data-testid="sa-otp-submit"
                className="w-full h-11 rounded-lg bg-[#D64D7A] hover:bg-[#c4406b] text-white text-sm font-semibold">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Verify & Sign In'}
              </Button>
            </form>

            <div className="text-center">
              {countdown > 0 ? (
                <p className="text-xs text-[#6b7280]">Resend code in <span className="text-white font-medium">{countdown}s</span></p>
              ) : (
                <button onClick={handleResend} disabled={resending} data-testid="sa-resend-otp"
                  className="text-xs text-[#D64D7A] hover:text-[#e96492] font-medium flex items-center gap-1.5 mx-auto transition-colors">
                  {resending ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                  Resend code
                </button>
              )}
            </div>
          </div>
        )}

        <button onClick={() => navigate('/login')} data-testid="sa-back-link"
          className="w-full text-center text-xs text-[#6b7280] hover:text-white mt-6 transition-colors">
          Back to main login
        </button>
      </div>
    </div>
  );
}
